// Asistent (kap. 34): rozhovor se streamovanou odpovědí (SSE z /ai/stream), nástroje provádí server; akce „připravit“
// jen otevře okno s předvyplněnými poli – uložit musí uživatel.
import { simpleBar } from '@core/components/chrome';
import { registerComponent } from '@core/views/factory';
import { action, getToken, url } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk } from '@core/i18n';
import { icon } from '@core/icons';

// context = výsledky nástrojů z kola (ze serveru, událost done); posílají se zpět, aby model v další otázce znal id a údaje
interface Msg { role: 'user' | 'assistant'; content: string; context?: unknown }
interface Payload { online: boolean; provider: string | null; budget: number; used: number; direct: boolean; admin: boolean; userName: string; examples: string[] }
interface Change { key: string; label: string; value: string; derived?: boolean }

registerComponent('ai-assistant', ({ desc, win, wm }) => {
  const p = desc.payload as unknown as Payload;
  const messages: Msg[] = [];
  const log = h('div', { class: 'ai-log' });
  const input = h('textarea', { class: 'input ai-input', rows: '2', placeholder: t('ai.assistant.placeholder') });
  const send = h('button', { type: 'button', class: 'btn primary ai-send', title: t('ai.assistant.send') }, icon('send'));
  const stop = h('button', { type: 'button', class: 'btn ai-stop', hidden: true }, t('ai.assistant.stop'));
  const clearBtn = h('button', { type: 'button', class: 'btn ghost sm' }, icon('refresh'), ' ', t('ai.assistant.clear'));
  const status = h('div', { class: 'ai-status muted small' });
  const foot = h('div', { class: 'ai-foot' }, h('div', { class: 'ai-compose' }, input, h('div', { class: 'ai-buttons' }, send, stop)), status);
  const head = h('div', { class: 'ai-head' }, h('span', { class: 'muted small' }, icon(p.online ? 'globe' : 'shield', 'icon'), ' ', p.online ? t('ai.assistant.hint_online', { provider: p.provider ?? '' }) : t('ai.assistant.hint_offline')), clearBtn);
  const el = h('div', { class: 'view ai-view' }, head, log, foot);
  let busy = false;
  let abort: AbortController | null = null;

  const bubble = (role: 'user' | 'assistant' | 'system', text: string): HTMLElement => {
    const b = h('div', { class: 'ai-msg ' + role }, h('div', { class: 'ai-msg-text' }, text));
    log.appendChild(b);
    log.scrollTop = log.scrollHeight;
    return b;
  };
  const intro = () => {
    clear(log);
    bubble('assistant', t(p.direct ? 'ai.assistant.intro_direct' : 'ai.assistant.intro', { name: p.userName.split(' ')[0] ?? p.userName }));
    const ex = h('div', { class: 'ai-examples' }, h('div', { class: 'muted small' }, t('ai.assistant.examples')), ...p.examples.map((x) => { const b = h('button', { type: 'button', class: 'ai-example' }, x); b.addEventListener('click', () => { input.value = x; void ask(); }); return b; }));
    log.appendChild(ex);
    updateStatus();
  };
  const updateStatus = () => {
    // spotřebu tokenů vidí zatím jen správce (rozhodnutí 26. 10.)
    status.textContent = p.admin ? t('ai.assistant.budget', { used: p.used.toLocaleString(), budget: p.budget > 0 ? t('ai.assistant.budget_of', { limit: p.budget.toLocaleString() }) : '' }) : '';
  };
  // přímý zápis proveden: co se změnilo (odvozené hodnoty zvýrazněné ke kontrole), Zpět a Otevřít
  const doneBox = (data: Record<string, unknown>): HTMLElement => {
    const changes = (data['changes'] ?? []) as Change[];
    const app = String(data['app']);
    const window = String(data['window']);
    const id = String(data['id'] ?? '');
    const list = h('div', { class: 'ai-changes' }, ...changes.map((c) => h('div', { class: 'ai-change' + (c.derived ? ' derived' : '') }, h('span', { class: 'muted' }, c.label + ': '), c.value, c.derived ? h('span', { class: 'ai-check muted' }, ' ' + t('ai.assistant.check')) : '')));
    const openBtn = h('button', { type: 'button', class: 'btn sm ghost' }, t('ai.assistant.open'));
    openBtn.addEventListener('click', () => void wm.open({ app, window, params: { id } }));
    const buttons = h('div', { class: 'ai-done-buttons' }, openBtn);
    if (data['undo'] === true) {
      const undoBtn = h('button', { type: 'button', class: 'btn sm' }, icon('undo', 'icon'), ' ', t('ai.assistant.undo'));
      undoBtn.addEventListener('click', async () => {
        undoBtn.disabled = true;
        try {
          await action('ai', 'asistent', 'undo', {}, { writeId: String(data['writeId'] ?? '') });
          undoBtn.replaceWith(h('span', { class: 'muted small' }, t('ai.assistant.undone')));
        } catch (e) {
          undoBtn.replaceWith(h('span', { class: 'ai-error small' }, t('ai.assistant.undo_failed', { message: tk((e as Error).message) })));
        }
      });
      buttons.prepend(undoBtn);
    }
    return h('div', { class: 'ai-action ai-done' }, h('div', { class: 'ai-done-head' }, icon('check', 'icon'), ' ', t(data['op'] === 'create' ? 'ai.assistant.done_create' : 'ai.assistant.done_update', { title: String(data['title'] ?? '') })), list, buttons);
  };

  async function ask(): Promise<void> {
    const text = input.value.trim();
    if (!text || busy) return;
    input.value = '';
    busy = true;
    send.disabled = true;
    stop.hidden = false;
    messages.push({ role: 'user', content: text });
    bubble('user', text);
    const reply = bubble('assistant', '');
    const replyText = reply.querySelector('.ai-msg-text') as HTMLElement;
    const working = h('div', { class: 'ai-working muted small' }, icon('sparkles', 'icon spin'), ' ', t('ai.assistant.thinking'));
    reply.appendChild(working);
    let answer = '';
    let context: unknown;
    abort = new AbortController();
    try {
      const token = getToken();
      const res = await fetch(url('/ai/stream'), { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Iwan-Token': token ?? '' }, body: JSON.stringify({ messages }), signal: abort.signal });
      if (!res.ok || !res.body) {
        let msg = 'HTTP ' + res.status;
        try { const j = (await res.json()) as { error?: { message?: string } }; msg = tk(j.error?.message ?? msg); } catch { /* bez těla */ }
        throw new Error(msg);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const ev = /^event: (\w+)/m.exec(chunk)?.[1] ?? 'message';
          const dataLine = chunk.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;
          const data = JSON.parse(dataLine.slice(5)) as Record<string, unknown>;
          if (ev === 'delta') {
            answer += String(data['text'] ?? '');
            replyText.textContent = answer;
            log.scrollTop = log.scrollHeight;
          } else if (ev === 'tool' && data['kind'] === 'read') {
            const args = (data['args'] ?? {}) as Record<string, unknown>;
            working.replaceChildren(icon('search', 'icon'), ' ', t('ai.assistant.searching', { name: String(args['query'] ?? data['name'] ?? '') }));
          } else if (ev === 'action') {
            const kind = String(data['kind']);
            const app = String(data['app']);
            const window = String(data['window']);
            if (kind === 'prepare') {
              const prefill = (data['prefill'] ?? {}) as Record<string, unknown>;
              const params = (data['params'] ?? {}) as Record<string, unknown>;
              void wm.open({ app, window, params, modal: false, restore: { prefill, edit: true } });
              reply.appendChild(h('div', { class: 'ai-action' }, icon('window', 'icon'), ' ', t('ai.assistant.prepared', { title: String(data['title'] ?? window) })));
            } else if (kind === 'open') {
              const params = (data['params'] ?? {}) as Record<string, unknown>;
              void wm.open({ app, window, params });
              reply.appendChild(h('div', { class: 'ai-action' }, icon('window', 'icon'), ' ', t('ai.assistant.opened', { window: String(data['title'] ?? window) })));
            } else if (kind === 'done') {
              reply.appendChild(doneBox(data));
            }
          } else if (ev === 'error') {
            throw new Error(tk(String(data['message'] ?? 'core.ai.failed')));
          } else if (ev === 'done') {
            p.used += Number(data['promptTokens'] ?? 0) + Number(data['completionTokens'] ?? 0);
            context = data['context'];
            updateStatus();
          }
        }
      }
      if (!answer.trim()) replyText.textContent = '…';
      messages.push({ role: 'assistant', content: answer, context });
    } catch (e) {
      if ((e as Error).name !== 'AbortError') replyText.replaceChildren(h('span', { class: 'ai-error' }, t('ai.assistant.error', { message: (e as Error).message })));
      if (answer) messages.push({ role: 'assistant', content: answer, context });
    } finally {
      working.remove();
      busy = false;
      send.disabled = false;
      stop.hidden = true;
      abort = null;
      input.focus();
    }
  }

  send.addEventListener('click', () => void ask());
  stop.addEventListener('click', () => abort?.abort());
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(); } });
  clearBtn.addEventListener('click', () => { messages.length = 0; intro(); });
  // nový vzhled: Nový rozhovor v liště záhlaví
  const sb = simpleBar(win, wm, () => [{ id: 'clear', label: t('ai.assistant.clear'), icon: 'refresh', place: 'action', onClick: () => clearBtn.click() }]);
  if (sb.active) clearBtn.hidden = true;
  intro();
  win.setTitle(tk(desc.title), 'sparkles');

  return { el, focus: () => input.focus(), getState: () => null, setState: () => undefined, destroy: () => abort?.abort() };
});
